import { useEffect, useMemo, useState } from 'react';

import classNames from 'classnames';
import { formatDistanceToNow } from 'date-fns';

import { localDate } from '@iconik-io/core/src/date/format';
import type {
  AssetsShareElasticSchemaWithIdNullable,
  AssetsShareUserSchemaWithId,
} from '@iconik-io/iconik-api/src/services/assets';
import { SHARE_USER_EMAIL_PLACEHOLDER } from '@iconik-io/shared-lib/src/react/components/shared/segment/constants';
import { usePlaylist } from '@iconik-io/shared-lib/src/react/hooks/entities/use_playlist';

import { isContextMenuEmpty } from '../../../components/context_menu/utils';
import SelectableItems from '../../../components/selectable_items/selectable_items';
import { createListItemsSelector } from '../../../ducks/common_list/selectors';
import { getShareUsers } from '../../../ducks/shares_users/thunks';
import {
  useAppDispatch,
  useAppSelector,
} from '../../../store/react_redux_typed';
import LoadingBar from '../../../ui/loading_bar/loading_bar';
import OpenContextMenuButton from '../../../ui/table/open_context_menu_button/open_context_menu_button';
import SharePanelUsersContextMenu, { getDefinition } from './context_menu';

import styles from '../shares.module.css';

type ShareUsersProps = {
  shareId: string;
  colSpan: number;
  shares: AssetsShareElasticSchemaWithIdNullable[];
};

type ShareLinkData = {
  accessCount: number;
  lastAccessDate: string | null;
};

const EMPTY_SHARE_USER_IDS: string[] = [];

export function ShareUsers({ colSpan, shareId, shares }: ShareUsersProps) {
  const dispatch = useAppDispatch();
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const selectShareUsers = useMemo(
    () => createListItemsSelector<AssetsShareUserSchemaWithId>(),
    [],
  );

  const users = useAppSelector((state) => {
    const shareUserIds = state.sharesUsers[shareId] ?? EMPTY_SHARE_USER_IDS;
    return selectShareUsers(state.entities.share_users, shareUserIds);
  });

  const contextMenuEmpty = useAppSelector((state) =>
    isContextMenuEmpty(getDefinition, state, users),
  );

  const share = shares.find((item) => item.id === shareId);
  const shareObjectType = share?.object_type;
  const shareObjectId = share?.object_id;

  const playlist = usePlaylist(
    shareObjectType === 'playlists' && shareObjectId
      ? shareObjectId
      : undefined,
  );

  useEffect(() => {
    if (!shareObjectType || !shareObjectId) {
      return;
    }
    dispatch(getShareUsers(shareObjectType, shareObjectId, shareId)).finally(
      () => setIsLoading(false),
    );
  }, [shareObjectType, shareObjectId, shareId]);

  const shareLinkData = useMemo(() => {
    if (!share?.users || share.users.length === 0) {
      return { accessCount: 0, lastAccessDate: null };
    }

    const result: ShareLinkData = {
      accessCount: 0,
      lastAccessDate: null,
    };

    for (const user of share.users) {
      result.accessCount += user.access_count || 0;
      if (user.last_access_date) {
        if (
          !result.lastAccessDate ||
          new Date(user.last_access_date) > new Date(result.lastAccessDate)
        ) {
          result.lastAccessDate = user.last_access_date;
        }
      }
    }

    return result;
  }, [share?.users]);

  const { accessCount, lastAccessDate } = shareLinkData;

  if (isLoading) {
    return (
      <td colSpan={colSpan}>
        <LoadingBar className={styles.loading} />
      </td>
    );
  }
  if (users.length === 0) {
    return (
      <td colSpan={colSpan}>
        <p className={styles.empty}>This share has no users</p>
      </td>
    );
  }

  return (
    <td colSpan={colSpan} className={styles.list_items}>
      <SelectableItems
        onSelectIds={setSelectedIds}
        items={users}
        multiselect={false}
        isSelectAllAllowed={false}
        contextMenu={!share?.url ? SharePanelUsersContextMenu : undefined}
      >
        <p className={styles.title}>{share?.title}</p>
        {playlist?.entity ? (
          <p className={styles.playlist}>
            This Playlist contains{' '}
            {playlist.entity.item_count === 1
              ? '1 item'
              : `${playlist.entity.item_count} items`}
          </p>
        ) : null}
        <div className={styles.list_items}>
          {share?.url ? (
            <>
              <p className={styles.share_type_heading}>Share URL:</p>
              <div className={styles.url}>{share.url}</div>
              <div className={styles.url_data}>
                {accessCount ? `${accessCount} visits. ` : 'Not seen'}
                {lastAccessDate
                  ? `Last one ${formatDistanceToNow(localDate(lastAccessDate), {
                      addSuffix: true,
                    })}`
                  : ''}
              </div>
            </>
          ) : null}

          {users.some((user) => user.email !== SHARE_USER_EMAIL_PLACEHOLDER) ? (
            <>
              <p className={styles.share_type_heading}>Shared with:</p>
              {users
                .filter((user) => user.email !== SHARE_USER_EMAIL_PLACEHOLDER)
                .map(({ id, email, access_count, last_access_date }) => (
                  <div
                    data-id={id}
                    key={id}
                    className={classNames(styles.list_item, {
                      [styles.selected]: id && selectedIds.indexOf(id) >= 0,
                    })}
                  >
                    <div className={styles.user_data}>
                      <div className={styles.email}>{email}</div>
                      {access_count ? `${access_count} visits. ` : 'Not seen'}
                      {last_access_date
                        ? `Last one ${formatDistanceToNow(
                            localDate(last_access_date),
                            { addSuffix: true },
                          )}`
                        : ''}
                    </div>
                    {contextMenuEmpty || share?.url ? null : (
                      <OpenContextMenuButton />
                    )}
                  </div>
                ))}
            </>
          ) : null}
        </div>
      </SelectableItems>
    </td>
  );
}
